import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CreditCard, Wrench, Clock, HelpCircle } from 'lucide-react';
import { tenantPortalAPI } from '../services/api';

const HelpModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-blue-600" />
          How to Find Your Tenant ID
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700 mb-3">
            Your <strong>Tenant ID</strong> and <strong>Access Code</strong> were provided by your property manager.
          </p>
        </div>

        <div className="space-y-3">
          <div className="font-medium text-gray-900">Where to find your credentials:</div>
          <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
            <li>
              <strong>Check your email</strong> - Your property manager should have sent you a welcome email with your Tenant ID and Access Code.
            </li>
            <li>
              <strong>Look at your lease documents</strong> - Some property managers include portal credentials in the lease paperwork.
            </li>
            <li>
              <strong>Contact your property manager</strong> - If you can't find your credentials, reach out to Legacy Property Association or your property manager directly.
            </li>
          </ol>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            💡 <strong>Tip:</strong> After signing in once, check "Remember this device" so you won't need to enter these credentials again on this device.
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-600">
            <strong>Example format:</strong><br/>
            Tenant ID: <code className="bg-gray-200 px-2 py-1 rounded">david-tenant-1</code><br/>
            Access Code: <code className="bg-gray-200 px-2 py-1 rounded">abc12345</code>
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700">
            Got it, thanks!
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const TenantHub = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [tenantId, setTenantId] = useState(localStorage.getItem('tenant_id') || '');
  const [accessCode, setAccessCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const authed = !!(localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token'));

  const login = async () => {
    setError('');
    setIsLoggingIn(true);
    try {
      await tenantPortalAPI.login({ 
        tenant_id: tenantId.trim(), 
        access_code: accessCode.trim(), 
        remember 
      });
      // Force page refresh to update authentication state
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Sign in failed');
      setIsLoggingIn(false);
      return;
    }
  };

  const logout = async () => {
    try { await tenantPortalAPI.revoke(); } catch {}
    localStorage.removeItem('tenant_token');
    sessionStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_id');
    // Force page refresh to update authentication state
    window.location.reload();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Hero */}
      <div className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
        <h1 className="text-3xl font-bold">Welcome to Legacy Property Tenant Portal</h1>
        <p className="mt-2 text-white/90">Sign in to make payments, submit work orders, and view your history.</p>
      </div>

      {/* Sign In / Status */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sign In</CardTitle>
          <button className="flex items-center text-sm text-blue-600 hover:underline" onClick={() => setHelpOpen(true)}>
            <HelpCircle className="h-4 w-4 mr-1" /> Need help finding your Tenant ID?
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          {!authed ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input placeholder="Tenant ID" value={tenantId} onChange={(e)=>setTenantId(e.target.value)} />
                <Input placeholder="Access Code" value={accessCode} onChange={(e)=>setAccessCode(e.target.value)} />
                <Button onClick={login} disabled={isLoggingIn}>
                  {isLoggingIn ? 'Signing In...' : 'Sign In'}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input id="remember" type="checkbox" checked={remember} onChange={(e)=>setRemember(e.target.checked)} />
                <label htmlFor="remember">Remember this device</label>
              </div>
              {error && <div className="text-sm text-red-600">{error}</div>}
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">Signed in as <span className="font-medium text-gray-900">{localStorage.getItem('tenant_id')}</span></div>
              <div className="space-x-2">
                <Button variant="outline" onClick={logout}>Sign Out</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to={`/o/${orgId}/tenant/payment`} className={`border rounded-lg p-4 hover:shadow transition ${!authed ? 'pointer-events-none opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded bg-blue-50"><CreditCard className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <div className="font-medium">Pay Rent</div>
                  <div className="text-xs text-gray-600">Pay by card with Stripe or with PayPal</div>
                </div>
              </div>
            </Link>
            <Link to={`/o/${orgId}/tenant/maintenance`} className={`border rounded-lg p-4 hover:shadow transition ${!authed ? 'pointer-events-none opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded bg-yellow-50"><Wrench className="h-5 w-5 text-yellow-600" /></div>
                <div>
                  <div className="font-medium">Submit Work Order</div>
                  <div className="text-xs text-gray-600">Describe the issue and upload photos</div>
                </div>
              </div>
            </Link>
            <Link to={`/o/${orgId}/tenant/history`} className={`border rounded-lg p-4 hover:shadow transition ${!authed ? 'pointer-events-none opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded bg-purple-50"><Clock className="h-5 w-5 text-purple-600" /></div>
                <div>
                  <div className="font-medium">Payment History</div>
                  <div className="text-xs text-gray-600">View your recent payments and receipts</div>
                </div>
              </div>
            </Link>
          </div>
          {!authed && (
            <div className="text-xs text-gray-500 mt-3">Sign in to enable quick actions.</div>
          )}
        </CardContent>
      </Card>

      {/* Help Modal */}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  );
};

export default TenantHub;